import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';

/**
 * DateInput — بديل مباشر لـ <input type="date"> يعرض التاريخ dd/mm/yyyy دائماً.
 *
 * لماذا مكوّن مخصص؟ لأن حقل التاريخ الأصلي في المتصفح يعرض التاريخ بصيغة
 * لغة واجهة المتصفح (mm/dd/yyyy على الإنجليزية الأمريكية)، ولا تملك الصفحة
 * أي وسيلة لتغيير ذلك — لا خاصية lang ولا CSS ولا JavaScript.
 *
 * العقد مطابق للحقل الأصلي تماماً ليكون الاستبدال آلياً:
 *   value    : 'yyyy-mm-dd' (أو '')
 *   onChange : تستقبل { target: { value: 'yyyy-mm-dd', name } }
 * فتبقى كل الاستدعاءات القائمة `onChange={(e) => setX(e.target.value)}` كما هي،
 * وتبقى حمولات الـ API بصيغة ISO دون تغيير.
 *
 * الإدخال متسامح: يقبل 20/08/2026 و20-08-2026 و20082026، ويضيف الشرطات تلقائياً.
 */

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'yyyy-mm-dd' → 'dd/mm/yyyy' */
function isoToDisplay(iso) {
  const match = ISO_RE.exec(String(iso || ''));
  if (!match) return '';
  const [, y, m, d] = match;
  return `${d}/${m}/${y}`;
}

/**
 * 'dd/mm/yyyy' → 'yyyy-mm-dd'، أو null إذا كان التاريخ ناقصاً أو غير موجود
 * (مثل 31/02). يتحقق من التقويم لا من الشكل فقط.
 */
function displayToIso(text) {
  const digits = String(text || '').replace(/\D/g, '');
  if (digits.length !== 8) return null;

  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (month < 1 || month > 12 || day < 1 || year < 1000) return null;

  // يوم 31 في شهر من 30 يوماً يجعل Date يتجاوز إلى الشهر التالي — نرفضه
  const probe = new Date(year, month - 1, day);
  if (probe.getMonth() !== month - 1 || probe.getDate() !== day) return null;

  const pad = (n) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** يضيف الشرطات أثناء الكتابة: 2008 → 20/08 */
function maskWhileTyping(text) {
  const digits = String(text || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export default function DateInput({
  value,
  onChange,
  name,
  className = '',
  placeholder = 'يوم/شهر/سنة',
  disabled,
  required,
  min,
  max,
  ...rest
}) {
  const [text, setText] = useState(() => isoToDisplay(value));
  const nativeRef = useRef(null);
  // نتتبّع آخر قيمة أرسلناها للأب حتى لا نطمس ما يكتبه المستخدم
  // حين يعيد الأب تمرير القيمة نفسها
  const lastEmitted = useRef(isoToDisplay(value));

  useEffect(() => {
    const incoming = isoToDisplay(value);
    if (incoming !== lastEmitted.current) {
      setText(incoming);
      lastEmitted.current = incoming;
    }
  }, [value]);

  const emit = (iso) => {
    lastEmitted.current = isoToDisplay(iso);
    onChange?.({ target: { value: iso, name } });
  };

  const handleTextChange = (e) => {
    const masked = maskWhileTyping(e.target.value);
    setText(masked);

    if (masked === '') {
      emit('');
      return;
    }
    const iso = displayToIso(masked);
    if (iso) emit(iso);
  };

  // عند مغادرة الحقل: إمّا تاريخ صالح أو تنظيف المدخل الناقص
  const handleBlur = () => {
    if (text === '') {
      if (value) emit('');
      return;
    }
    const iso = displayToIso(text);
    if (iso) {
      setText(isoToDisplay(iso));
    } else {
      setText(isoToDisplay(value));
    }
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el || disabled) return;
    // showPicker غير مدعومة في سفاري — نعود للتركيز على الحقل النصي
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        /* بعض المتصفحات ترفض الاستدعاء خارج إيماءة المستخدم */
      }
    }
    el.focus();
    el.click();
  };

  const isIncomplete = useMemo(
    () => text !== '' && displayToIso(text) === null,
    [text]
  );

  return (
    <span className="relative inline-block w-full">
      <input
        type="text"
        inputMode="numeric"
        dir="ltr"
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-invalid={isIncomplete || undefined}
        className={className}
        // حشو فيزيائي متماثل لا منطقي: الحقل dir=ltr بينما الحاوية RTL،
        // فالخصائص المنطقية تضع الحشو والزر على جهتين متقابلتين.
        // التماثل يُبقي النص موسّطاً فعلاً ولا يتداخل مع أيقونة التقويم.
        style={{ textAlign: 'center', paddingLeft: '2.25rem', paddingRight: '2.25rem' }}
        {...rest}
      />

      {/* زر التقويم — يفتح منتقي المتصفح الأصلي عبر حقل مخفي */}
      <button
        type="button"
        tabIndex={-1}
        onClick={openPicker}
        disabled={disabled}
        aria-label="فتح التقويم"
        title="فتح التقويم"
        // موضع فيزيائي ثابت على اليمين ليطابق الحشو الأيمن أعلاه
        style={{ right: '0.5rem' }}
        className="absolute top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-40 dark:hover:text-gray-200"
      >
        <CalendarDays className="h-4 w-4" />
      </button>

      {/* الحقل الأصلي مخفي بصرياً: نستخدمه للتقويم فقط، لا للعرض */}
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={ISO_RE.test(String(value || '')) ? value : ''}
        min={min}
        max={max}
        onChange={(e) => {
          const iso = e.target.value;
          setText(isoToDisplay(iso));
          emit(iso);
        }}
        className="pointer-events-none absolute bottom-0 h-0 w-0 opacity-0"
      />
    </span>
  );
}
